import { useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { CreateTicketInput } from '@/api/ticketsApi'
import { Field, NativeSelect } from '@/components/common/Field'
import { errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useContacts, useCustomers, useProducts } from '@/hooks/useDirectory'
import { useApplicableSlaPolicy, useCreateTicket } from '@/hooks/useTickets'
import { defaultTenantId, formatBudget } from '@/lib/manualTicket'
import { FILTER_PRIORITIES, FILTER_TYPES } from '@/lib/queueFilters'
import { priorityLabel, typeLabel } from '@/lib/ticketLabels'
import { useAuth } from '@/providers/AuthProvider'

interface FormState {
  customerId: string
  environmentId: string
  /** Agents: '' = from the reporter, else a tenant of the customer. */
  tenantId: string
  contactId: string
  productId: string
  componentId: string
  type: string
  priority: string
  subject: string
  description: string
  withRecord: boolean
  recordType: string
  recordKey: string
  recordUrl: string
}

const EMPTY: FormState = {
  customerId: '',
  environmentId: '',
  tenantId: '',
  contactId: '',
  productId: '',
  componentId: '',
  type: 'defect',
  priority: 'P3',
  subject: '',
  description: '',
  withRecord: false,
  recordType: '',
  recordKey: '',
  recordUrl: '',
}

type Errors = Partial<Record<'customerId' | 'contactId' | 'productId' | 'subject' | 'description' | 'record', string>>

/**
 * Desk-entered ticket (plan §13 phase 2). Agents pick the customer and the
 * reporting contact; a customer principal files for their own organisation and
 * is the reporter. On success it navigates to the new ticket.
 */
export function NewTicketDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isCustomer = user?.kind === 'CUSTOMER'
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Errors>({})
  const create = useCreateTicket()

  const customers = useCustomers(open)
  const products = useProducts(open)
  const customerId = isCustomer ? (user?.customerId ?? '') : form.customerId
  const contacts = useContacts(isCustomer ? null : customerId || null)

  const customer = customers.data?.find((c) => c.id === customerId)
  const tenants = customer?.tenants ?? []
  const reporterTenant = tenants.find((x) => x.id === contacts.data?.find((c) => c.id === form.contactId)?.tenantId)
  const policy = useApplicableSlaPolicy(
    { priority: form.priority, customerId, productId: form.productId },
    open && !isCustomer,
  ).data
  const product = products.data?.find((p) => p.id === form.productId)
  const environments = useMemo(
    () => (customer?.environments ?? []).filter((e) => !form.productId || e.productId === form.productId),
    [customer, form.productId],
  )

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }))

  function reset() {
    setForm(EMPTY)
    setErrors({})
    create.reset()
  }

  function validate(): Errors {
    const e: Errors = {}
    if (!isCustomer && !form.customerId) e.customerId = t('newTicket.errors.customer')
    if (!isCustomer && !form.contactId) e.contactId = t('newTicket.errors.reporter')
    if (!form.productId) e.productId = t('newTicket.errors.product')
    if (!form.subject.trim()) e.subject = t('newTicket.errors.subject')
    if (!form.description.trim()) e.description = t('newTicket.errors.description')
    if (form.withRecord && (!form.recordType.trim() || !form.recordKey.trim())) e.record = t('newTicket.errors.record')
    return e
  }

  function onSubmit(ev: FormEvent) {
    ev.preventDefault()
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length > 0) return
    const input: CreateTicketInput = {
      subject: form.subject.trim(),
      description: form.description.trim(),
      type: form.type,
      priority: form.priority,
      productId: form.productId,
      componentId: form.componentId || undefined,
      environmentId: form.environmentId || undefined,
      channel: isCustomer ? 'portal' : 'desk',
    }
    if (!isCustomer) {
      input.customerId = form.customerId
      input.reporter = { contactId: form.contactId }
      if (form.tenantId) input.tenantId = form.tenantId
    }
    if (form.withRecord) {
      input.linkedRecord = {
        product: product?.key ?? '',
        type: form.recordType.trim(),
        key: form.recordKey.trim(),
        url: form.recordUrl.trim() || undefined,
      }
    }
    create.mutate(input, {
      onSuccess: (detail) => {
        onOpenChange(false)
        reset()
        navigate(`/tickets/${detail.key}`)
      },
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o)
        if (!o) reset()
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('newTicket.title')}</DialogTitle>
          <DialogDescription>{t('newTicket.description')}</DialogDescription>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={onSubmit} noValidate>
          {!isCustomer ? (
            <>
              <Field id="nt-customer" label={t('newTicket.customer')} error={errors.customerId}>
                <NativeSelect
                  id="nt-customer"
                  value={form.customerId}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      customerId: e.target.value,
                      environmentId: '',
                      tenantId: '',
                      contactId: '',
                    }))
                  }
                >
                  <option value="">{t('common.select')}</option>
                  {customers.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field id="nt-contact" label={t('newTicket.reporter')} error={errors.contactId}>
                <NativeSelect
                  id="nt-contact"
                  value={form.contactId}
                  disabled={!form.customerId}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      contactId: e.target.value,
                      tenantId: defaultTenantId(
                        contacts.data?.find((c) => c.id === e.target.value),
                        tenants,
                      ),
                    }))
                  }
                >
                  <option value="">{form.customerId ? t('common.select') : t('newTicket.pickCustomerFirst')}</option>
                  {contacts.data?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.role ? `${c.name} · ${c.role}` : c.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field id="nt-tenant" label={t('newTicket.tenant')}>
                <NativeSelect
                  id="nt-tenant"
                  value={form.tenantId}
                  disabled={!customer || tenants.length === 0}
                  onChange={(e) => set('tenantId', e.target.value)}
                >
                  <option value="">
                    {reporterTenant
                      ? t('manualTicket.tenantFromReporterNamed', { tenant: reporterTenant.name })
                      : t('manualTicket.tenantFromReporter')}
                  </option>
                  {tenants.map((x) => (
                    <option key={x.id} value={x.id}>
                      {`${x.name} (${x.code})`}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <div aria-hidden />
            </>
          ) : null}

          <Field id="nt-product" label={t('newTicket.product')} error={errors.productId}>
            <NativeSelect
              id="nt-product"
              value={form.productId}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  productId: e.target.value,
                  componentId: '',
                  environmentId: '',
                }))
              }
            >
              <option value="">{t('common.select')}</option>
              {products.data
                ?.filter((p) => p.active !== false)
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
            </NativeSelect>
          </Field>
          <Field id="nt-component" label={t('newTicket.component')}>
            <NativeSelect id="nt-component" value={form.componentId} disabled={!product} onChange={(e) => set('componentId', e.target.value)}>
              <option value="">{product ? t('newTicket.noComponent') : t('newTicket.pickProductFirst')}</option>
              {product?.components.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </NativeSelect>
          </Field>

          <Field id="nt-env" label={t('newTicket.environment')}>
            <NativeSelect id="nt-env" value={form.environmentId} disabled={!customer} onChange={(e) => set('environmentId', e.target.value)}>
              <option value="">{customer ? t('newTicket.noEnvironment') : t('newTicket.pickCustomerFirst')}</option>
              {environments.map((env) => (
                <option key={env.id} value={env.id}>
                  {[env.product?.name, env.name].filter(Boolean).join(' · ')}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field id="nt-type" label={t('newTicket.type')}>
              <NativeSelect id="nt-type" value={form.type} onChange={(e) => set('type', e.target.value)}>
                {FILTER_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {typeLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
            <Field
              id="nt-priority"
              label={t('newTicket.priority')}
              help={
                policy
                  ? t('newTicket.slaHint', {
                      policy: policy.name,
                      fr: formatBudget(policy.firstResponseMins),
                      res: formatBudget(policy.resolutionMins),
                    })
                  : undefined
              }
            >
              <NativeSelect id="nt-priority" value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                {FILTER_PRIORITIES.map((v) => (
                  <option key={v} value={v}>
                    {priorityLabel(v)}
                  </option>
                ))}
              </NativeSelect>
            </Field>
          </div>

          <Field id="nt-subject" label={t('newTicket.subject')} error={errors.subject} className="col-span-2">
            <Input id="nt-subject" value={form.subject} maxLength={300} onChange={(e) => set('subject', e.target.value)} />
          </Field>
          <Field id="nt-description" label={t('newTicket.descriptionLabel')} error={errors.description} className="col-span-2">
            <Textarea id="nt-description" rows={5} value={form.description} onChange={(e) => set('description', e.target.value)} />
          </Field>

          <div className="col-span-2 flex flex-col gap-3 rounded-md border border-line p-3">
            <label className="flex items-center gap-2 text-meta text-ink">
              <input type="checkbox" checked={form.withRecord} onChange={(e) => set('withRecord', e.target.checked)} />
              {t('newTicket.addLinkedRecord')}
            </label>
            {form.withRecord ? (
              <div className="grid grid-cols-3 gap-3">
                <Field id="nt-rtype" label={t('recordLink.type')}>
                  <Input
                    id="nt-rtype"
                    placeholder={product?.keyPatterns?.[0]?.type ?? t('recordLink.typePlaceholder')}
                    value={form.recordType}
                    onChange={(e) => set('recordType', e.target.value)}
                  />
                </Field>
                <Field id="nt-rkey" label={t('recordLink.key')}>
                  <Input
                    id="nt-rkey"
                    className="font-mono"
                    placeholder={t('recordLink.keyPlaceholder')}
                    value={form.recordKey}
                    onChange={(e) => set('recordKey', e.target.value)}
                  />
                </Field>
                <Field id="nt-rurl" label={`${t('recordLink.url')} (${t('common.optional')})`}>
                  <Input id="nt-rurl" type="url" value={form.recordUrl} onChange={(e) => set('recordUrl', e.target.value)} />
                </Field>
                {errors.record ? (
                  <p role="alert" className="col-span-3 text-label text-status-escalated-fg">
                    {errors.record}
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>

          {create.error ? (
            <p role="alert" className="col-span-2 rounded-md bg-status-escalated-bg px-3 py-2 text-meta text-status-escalated-fg">
              {t('common.error', { message: errorMessage(create.error) })}
            </p>
          ) : null}

          <DialogFooter className="col-span-2 items-center sm:justify-between">
            <Link
              to="/tickets/new"
              onClick={() => onOpenChange(false)}
              title={t('newTicket.fullFormHint')}
              className="text-meta font-medium text-ink-muted hover:text-ink hover:underline"
            >
              {t('newTicket.fullForm')}
            </Link>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? t('newTicket.submitting') : t('newTicket.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
